import { supabase } from './supabase';
import { postJournal, voidJournal } from './journal';
import { ACCOUNTS } from './accounts';
import { getClientArBalance, computeFifoAllocation, getInvoicePaidAmount } from './receiptAllocations';

const invoiceTotal = (inv) => (inv.line_items || []).reduce((s, li) =>
  s + (parseFloat(li.unit_price ?? li.unitPrice ?? li.amount ?? 0) * parseFloat(li.qty ?? li.quantity ?? 1)), 0);

// A commission's reference_id can point at the invoice itself or at the invoice's source record.
export async function loadInvoicesForCommissions(commissions) {
  const refIds = [...new Set(commissions.map(c => c.reference_id).filter(Boolean))];
  const byRef = {};
  const CHUNK = 100; // keep the .or() URL under PostgREST's length limit
  for (let i = 0; i < refIds.length; i += CHUNK) {
    const slice = refIds.slice(i, i + CHUNK);
    const { data } = await supabase.from('invoices')
      .select('id, invoice_number, issued_date, status, line_items, source_id, clients(full_name, client_code)')
      .or(`id.in.(${slice.join(',')}),source_id.in.(${slice.join(',')})`);
    (data || []).forEach(inv => { byRef[inv.id] = inv; if (inv.source_id) byRef[inv.source_id] = inv; });
  }
  const withPaid = {};
  await Promise.all(Object.values(byRef).map(async inv => {
    if (withPaid[inv.id]) return;
    const total = invoiceTotal(inv);
    const paid = inv.status === 'VOID' ? 0 : await getInvoicePaidAmount(inv.id);
    const bal = total - paid;
    withPaid[inv.id] = inv.status === 'VOID' ? 'Void' : bal <= 0.01 ? 'Paid' : paid > 0.01 ? 'Partially Paid' : 'Unpaid';
  }));
  return Object.fromEntries(Object.entries(byRef).map(([ref, inv]) => [ref, { ...inv, payStatus: withPaid[inv.id] }]));
}

// Same rule as CommissionLedger's Approve: the expense/payable journal is posted once, dated to the
// invoice (so it lands in the period it was incurred). Never posts a second one for the same commission.
async function ensureApproved(c, invoice, agent, username) {
  const { data: existing } = await supabase.from('journal_entries')
    .select('id').eq('reference_type', 'COMMISSION_APPROVAL').eq('reference_id', c.id).limit(1);
  const amt = parseFloat(c.commission_amount) || 0;
  if (!existing?.length && amt > 0) {
    await postJournal({
      date: invoice?.issued_date || c.created_at?.slice(0, 10) || new Date().toISOString().slice(0, 10),
      description: `Agent commission: ${agent.full_name} — ${invoice?.invoice_number || c.product_type}`,
      reference_type: 'COMMISSION_APPROVAL',
      reference_id: c.id,
      exchange_rate: 1,
      lines: [
        { account_id: ACCOUNTS.COMMISSION_EXPENSE, debit_amount: amt, credit_amount: 0, description: 'Commission expense' },
        { account_id: ACCOUNTS.COMMISSION_PAYABLE, debit_amount: 0, credit_amount: amt, description: 'Commission payable', party_type: 'agent', party_id: agent.id },
      ],
      created_by: username,
    });
  }
  if (c.commission_status === 'PENDING') {
    await supabase.from('commissions').update({ commission_status: 'APPROVED', approved_at: new Date().toISOString() }).eq('id', c.id);
  }
}

/**
 * Set off part (or all) of what an agent is owed in commission against what he owes as a client:
 *   DR Commission Payable (2070, tagged to the agent) / CR Accounts Receivable (1030, tagged to the client)
 * The amount is chosen by the user and drawn from his unpaid commissions oldest-first (or from just the
 * ones passed in commissionIds). Each commission tracks how much of it has been settled
 * (commissions.settled_amount), so a commission can be used in part — only a fully settled one becomes
 * PAID, the rest stay APPROVED with the remainder still owed to him. Pending commissions drawn from are
 * approved in full first (their expense/payable journal posts as usual), since the commission is fully
 * earned even if only part of it is set off. The credit is also recorded as receipt_allocations (oldest
 * opening balance / invoices first) so the client's invoices show as settled, not just the ledger
 * balance. Fully reversible via reverseSetOff().
 */
export async function setOffCommissions({ agent, clientId, amount, commissionIds, username }) {
  const setAmount = Math.round((parseFloat(amount) || 0) * 100) / 100;
  if (setAmount <= 0) throw new Error('Enter an amount to set off.');

  let q = supabase.from('commissions').select('*').eq('agent_ref_id', agent.id).in('commission_status', ['PENDING', 'APPROVED']).order('created_at', { ascending: true });
  if (commissionIds?.length) q = q.in('id', commissionIds);
  const { data: all } = await q;
  const comms = (all || []).filter(c => (parseFloat(c.commission_amount) || 0) - (parseFloat(c.settled_amount) || 0) > 0.005);
  if (!comms.length) throw new Error('This agent has no unpaid commission to set off.');

  const available = comms.reduce((s, c) => s + (parseFloat(c.commission_amount) - (parseFloat(c.settled_amount) || 0)), 0);
  if (setAmount > available + 0.01) throw new Error(`Only LKR ${available.toFixed(2)} of commission is available to set off.`);
  const ar = await getClientArBalance(clientId);
  if (setAmount > ar + 0.01) throw new Error(`The amount (LKR ${setAmount.toFixed(2)}) exceeds what this client owes (LKR ${ar.toFixed(2)}).`);

  // Draw oldest-first until the amount is covered.
  let remaining = setAmount;
  const items = [];
  for (const c of comms) {
    if (remaining <= 0.005) break;
    const out = parseFloat(c.commission_amount) - (parseFloat(c.settled_amount) || 0);
    const take = Math.min(out, remaining);
    items.push({ id: c.id, take: Math.round(take * 100) / 100, c });
    remaining -= take;
  }

  const { data: client } = await supabase.from('clients').select('full_name, client_code').eq('id', clientId).single();
  const invMap = await loadInvoicesForCommissions(items.map(i => i.c));
  for (const it of items) await ensureApproved(it.c, invMap[it.c.reference_id], agent, username);

  // Work out which invoices/opening balance this clears BEFORE writing anything.
  const allocations = (await computeFifoAllocation(clientId, setAmount)).filter(a => a.target_type !== 'advance');

  const { data: so, error: soErr } = await supabase.from('commission_setoffs').insert({
    agent_ref_id: agent.id, client_id: clientId, amount: setAmount, created_by: username,
    commission_ids: items.map(i => i.id), items: items.map(i => ({ id: i.id, take: i.take })),
  }).select().single();
  if (soErr) throw soErr;

  const { data: lkr } = await supabase.from('currencies').select('id').eq('code', 'LKR').single();
  const desc = `Commission set-off — ${agent.tls_code} ${agent.full_name} vs ${client.client_code}`;
  const je = await postJournal({
    date: new Date().toISOString().slice(0, 10),
    description: desc,
    reference_type: 'COMMISSION_SETOFF',
    reference_id: so.id,
    currency_id: lkr?.id,
    exchange_rate: 1,
    created_by: username,
    lines: [
      { account_id: ACCOUNTS.COMMISSION_PAYABLE, debit_amount: setAmount, credit_amount: 0, description: desc, party_type: 'agent', party_id: agent.id },
      { account_id: ACCOUNTS.AR, debit_amount: 0, credit_amount: setAmount, description: desc, party_type: 'client', party_id: clientId },
    ],
  });

  if (allocations.length) {
    await supabase.from('receipt_allocations').insert(allocations.map(a => ({
      receipt_voucher_id: null, client_id: clientId, invoice_id: a.invoice_id, target_type: a.target_type,
      amount: a.amount, created_by: username, commission_setoff_id: so.id,
    })));
  }
  // Invoices this fully settles become PAID, same as a normal receipt would.
  const flipped = [];
  for (const a of allocations.filter(a => a.invoice_id)) {
    const { data: inv } = await supabase.from('invoices').select('id, status, line_items').eq('id', a.invoice_id).single();
    if (inv?.status === 'SENT' && (await getInvoicePaidAmount(inv.id)) >= invoiceTotal(inv) - 0.01) {
      await supabase.from('invoices').update({ status: 'PAID' }).eq('id', inv.id);
      flipped.push(inv.id);
    }
  }

  for (const it of items) {
    const settled = (parseFloat(it.c.settled_amount) || 0) + it.take;
    const done = settled >= parseFloat(it.c.commission_amount) - 0.01;
    await supabase.from('commissions').update({
      settled_amount: settled,
      commission_status: done ? 'PAID' : 'APPROVED',
      notes: `LKR ${settled.toFixed(2)} of ${parseFloat(it.c.commission_amount).toFixed(2)} set off against ${client.client_code} dues (${je.entry_number})`,
    }).eq('id', it.id);
  }
  await supabase.from('commission_setoffs').update({ journal_entry_id: je.id, reopened_on_reverse: flipped }).eq('id', so.id);

  return {
    total: setAmount, entryNumber: je.entry_number,
    commissionsUsed: items.length, commissionsFullySettled: items.filter(i => (parseFloat(i.c.settled_amount) || 0) + i.take >= parseFloat(i.c.commission_amount) - 0.01).length,
    commissionLeft: available - setAmount,
    invoicesCleared: allocations.filter(a => a.invoice_id).length,
    openingBalanceCleared: allocations.filter(a => a.target_type === 'old_balance').reduce((s, a) => s + a.amount, 0),
  };
}

export async function reverseSetOff(setoff, { reason, username }) {
  if (setoff.reversed_at) throw new Error('Already reversed.');
  if (setoff.journal_entry_id) await voidJournal(setoff.journal_entry_id, `Commission set-off reversed: ${reason}`, username);
  await supabase.from('receipt_allocations').delete().eq('commission_setoff_id', setoff.id);
  for (const invId of setoff.reopened_on_reverse || []) {
    const { data: inv } = await supabase.from('invoices').select('id, status, line_items').eq('id', invId).single();
    if (inv?.status === 'PAID' && (await getInvoicePaidAmount(inv.id)) < invoiceTotal(inv) - 0.01) {
      await supabase.from('invoices').update({ status: 'SENT' }).eq('id', inv.id);
    }
  }
  // Give back exactly what this set-off settled on each commission. They stay approved (earned and
  // payable) — the set-off had already approved them.
  for (const it of setoff.items || []) {
    const { data: c } = await supabase.from('commissions').select('settled_amount').eq('id', it.id).single();
    const settled = Math.max(0, (parseFloat(c?.settled_amount) || 0) - it.take);
    await supabase.from('commissions').update({ settled_amount: settled, commission_status: 'APPROVED', notes: null }).eq('id', it.id);
  }
  await supabase.from('commission_setoffs').update({ reversed_at: new Date().toISOString(), reversed_by: username, reverse_reason: reason }).eq('id', setoff.id);
}
